import { Flex, Text } from "@jsq/ds/core";
import { useGetQueryDateParametersQuery } from "~/graphql/supergraphHooks";
import { getDateParameterLabel } from "~/pages/Reporting/Settings/components/shared/DatePeriodSection/constants";
import { SPACING } from "~/styles/variables";
import type { DateParameter } from "~/pages/Reporting/Settings/components/shared/DatePeriodSection/types";

type QueryDateParametersSummaryProps = {
  queryId: number | null;
  arenaId: number | null;
};

const toLabels = (parameters: ReadonlyArray<DateParameter>): string[] =>
  parameters.map((parameter) => getDateParameterLabel(parameter));

/**
 * Read-only list of at-run dates the selected query requires (CUSTOM date
 * period parameters + DYNAMIC_DATE fields), via `finpubQueryDateParameters`.
 */
export const QueryDateParametersSummary = ({
  queryId,
  arenaId,
}: QueryDateParametersSummaryProps) => {
  const { data, loading, error } = useGetQueryDateParametersQuery({
    variables: { queryId: queryId ?? 0, arenaId },
    skip: queryId == null,
    fetchPolicy: "cache-and-network",
  });

  if (queryId == null) return null;

  if (error) {
    return (
      <Flex
        flexDirection="column"
        gap={SPACING.X_SMALL}
        data-testid="query-date-parameters-summary"
      >
        <Text variant="strong-md">Dates used by this query</Text>
        <Text variant="secondary-sm" data-testid="query-date-parameters-error">
          Unable to load dates used by this query.
        </Text>
      </Flex>
    );
  }

  if (loading && data == null) {
    return (
      <Flex
        flexDirection="column"
        gap={SPACING.X_SMALL}
        data-testid="query-date-parameters-summary"
      >
        <Text variant="strong-md">Dates used by this query</Text>
        <Text variant="secondary-sm">Loading…</Text>
      </Flex>
    );
  }

  const parameters = data?.finpubQueryDateParameters?.parameters ?? [];
  const labels = toLabels(parameters);

  if (labels.length === 0) return null;

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.X_SMALL}
      data-testid="query-date-parameters-summary"
    >
      <Text variant="strong-md">Dates used by this query</Text>
      <Text variant="secondary-sm" data-testid="query-date-parameters-list">
        {labels.join(", ")}
      </Text>
    </Flex>
  );
};
